import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit, Trash2 } from 'lucide-react';
import { AdminStoreItemResource } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import StoreItemFormModal from '../components/StoreItemFormModal';
import { useAuth } from '../contexts/AuthContext';

const StoreItems: React.FC = () => {
  const { canManage } = useAuth();
  const [items, setItems] = useState<AdminStoreItemResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<{ category: '' | 'sales' | 'other'; isActive: '' | 'true' | 'false' }>({
    category: '',
    isActive: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminStoreItemResource | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters: { category?: 'sales' | 'other'; isActive?: boolean } = {};
      if (filters.category) apiFilters.category = filters.category;
      if (filters.isActive !== '') apiFilters.isActive = filters.isActive === 'true';
      const response = await api.getAdminStoreItems(page, perPage, search || undefined, apiFilters);
      setItems(response.data);
      setTotal(response.meta.total);
    } catch (error) {
      console.error('Error loading store items:', error);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, filters]);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(1); }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDelete = async (id: string) => {
    setDeleteLoading(true);
    try {
      await api.deleteStoreItem(id);
      setDeletingId(null);
      loadItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const categoryBadge = (category: string) => {
    const styles: Record<string, string> = {
      sales: 'bg-blue-100 text-blue-700',
      other: 'bg-gray-100 text-gray-700',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${styles[category] ?? 'bg-gray-100 text-gray-700'}`}>
        {category}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Store Items</h1>
          <p className="mt-1 text-sm text-gray-500">Manage feature store items available for purchase.</p>
        </div>
        {canManage && (
          <button
            onClick={() => { setEditingItem(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Add Item
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or key..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <select
            value={filters.category}
            onChange={e => { setFilters(f => ({ ...f, category: e.target.value as '' | 'sales' | 'other' })); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            <option value="sales">Sales</option>
            <option value="other">Other</option>
          </select>
          <select
            value={filters.isActive}
            onChange={e => { setFilters(f => ({ ...f, isActive: e.target.value as '' | 'true' | 'false' })); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Loading store items..." />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <h3 className="mt-2 text-sm font-medium text-gray-900">No store items found</h3>
          <p className="mt-1 text-sm text-gray-500">Try adjusting your search or add a new item.</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Key', 'Category', 'Price', 'Duration', 'Active', 'Sort', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map(item => (
                  <React.Fragment key={item.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono">{item.key}</td>
                      <td className="px-4 py-3">{categoryBadge(item.category)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(item.price)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{item.durationType}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{item.sortOrder}</td>
                      <td className="px-4 py-3">
                        {canManage && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => { setEditingItem(item); setShowForm(true); }}
                              className="text-primary-600 hover:text-primary-800"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeletingId(item.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* Inline delete confirmation */}
                    {deletingId === item.id && (
                      <tr className="bg-red-50">
                        <td colSpan={8} className="px-4 py-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-red-700">Delete <strong>{item.name}</strong>? This cannot be undone.</span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setDeletingId(null)}
                                className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                disabled={deleteLoading}
                                className="px-3 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                              >
                                {deleteLoading ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-200">
            <Pagination
              currentPage={page}
              total={total}
              perPage={perPage}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      <StoreItemFormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingItem(null); }}
        item={editingItem}
        onSubmit={loadItems}
      />
    </div>
  );
};

export default StoreItems;
